import { NavLink, Link, Outlet } from "react-router";
import { Trans, useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { useAuth } from "../auth.js";
import { LanguageSelector } from "./LanguageSelector.js";

type IconName =
  | "customers"
  | "plans"
  | "models"
  | "usage"
  | "monitoring"
  | "errors"
  | "conversations"
  | "audit"
  | "swaps"
  | "releases"
  | "users"
  | "system-prompts";

function Icon({ name }: { name: IconName }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.75,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };
  switch (name) {
    case "customers":
      return (
        <svg {...common}>
          <rect x="4" y="3" width="16" height="18" rx="1" />
          <path d="M9 7h.01M12 7h.01M15 7h.01M9 11h.01M12 11h.01M15 11h.01M9 15h.01M15 15h.01" />
          <path d="M10 21v-4h4v4" />
        </svg>
      );
    case "plans":
      return (
        <svg {...common}>
          <path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
          <line x1="7" y1="7" x2="7.01" y2="7" />
        </svg>
      );
    case "models":
      return (
        <svg {...common}>
          <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z" />
          <path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z" />
          <path d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4" />
          <path d="M17.599 6.5a3 3 0 0 0 .399-1.375" />
          <path d="M6.003 5.125A3 3 0 0 0 6.401 6.5" />
        </svg>
      );
    case "usage":
      return (
        <svg {...common}>
          <line x1="18" y1="20" x2="18" y2="10" />
          <line x1="12" y1="20" x2="12" y2="4" />
          <line x1="6" y1="20" x2="6" y2="14" />
          <line x1="3" y1="20" x2="21" y2="20" />
        </svg>
      );
    case "monitoring":
      return (
        <svg {...common}>
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
        </svg>
      );
    case "errors":
      return (
        <svg {...common}>
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      );
    case "conversations":
      return (
        <svg {...common}>
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      );
    case "audit":
      return (
        <svg {...common}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h8" />
          <polyline points="14 2 14 8 20 8" />
          <circle cx="17" cy="17" r="3" />
          <line x1="21" y1="21" x2="19.1" y2="19.1" />
        </svg>
      );
    case "swaps":
      return (
        <svg {...common}>
          <polyline points="17 1 21 5 17 9" />
          <path d="M3 11V9a4 4 0 0 1 4-4h14" />
          <polyline points="7 23 3 19 7 15" />
          <path d="M21 13v2a4 4 0 0 1-4 4H3" />
        </svg>
      );
    case "releases":
      return (
        <svg {...common}>
          <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
          <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
          <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
          <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
        </svg>
      );
    case "users":
      return (
        <svg {...common}>
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <circle cx="18" cy="15" r="3" />
          <path d="m21.7 16.4-.9-.3" />
          <path d="m15.2 13.9-.9-.3" />
          <path d="m16.6 18.7.3-.9" />
          <path d="m19.1 12.2.3-.9" />
          <path d="m19.6 18.7-.4-1" />
          <path d="m16.8 12.3-.4-1" />
          <path d="m14.3 16.6 1-.4" />
          <path d="m20.7 13.8 1-.4" />
        </svg>
      );
    case "system-prompts":
      return (
        <svg {...common}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="8" y1="13" x2="16" y2="13" />
          <line x1="8" y1="17" x2="13" y2="17" />
        </svg>
      );
  }
}

interface NavItem {
  to: string;
  label: MessageDescriptor;
  icon: IconName;
}

interface NavGroup {
  key: string;
  label: MessageDescriptor;
  items: NavItem[];
  // When true, only superusers see this group. Resellers get a trimmed nav that
  // matches what the cloud API actually accepts from them (403s from a hidden
  // route are worse UX than never showing it).
  superuserOnly?: boolean;
}

const NAV_GROUPS: NavGroup[] = [
  {
    key: "clients",
    label: msg`Clients`,
    items: [{ to: "/customers", label: msg`Customers`, icon: "customers" }],
  },
  {
    key: "billing",
    label: msg`Billing`,
    items: [
      { to: "/plans", label: msg`Plans`, icon: "plans", superuserOnly: true },
      { to: "/llm-models", label: msg`Models & prices`, icon: "models", superuserOnly: true },
      // Usage stays visible to resellers — the API filters it to their customers.
      { to: "/usage", label: msg`Usage`, icon: "usage" },
    ] as (NavItem & { superuserOnly?: boolean })[],
  },
  {
    key: "operations",
    label: msg`Operations`,
    items: [
      { to: "/monitoring", label: msg`Monitoring`, icon: "monitoring", superuserOnly: true },
      // Conversations stays visible to resellers — filtered by the API.
      { to: "/conversations", label: msg`Conversations`, icon: "conversations" },
      // Errors stays visible to resellers — API scopes rows to their customers.
      { to: "/errors", label: msg`Errors`, icon: "errors" },
      { to: "/audit-log", label: msg`Audit log`, icon: "audit", superuserOnly: true },
      { to: "/license-swaps", label: msg`License swaps`, icon: "swaps", superuserOnly: true },
    ] as (NavItem & { superuserOnly?: boolean })[],
  },
  {
    key: "system",
    label: msg`System`,
    // Releases visible to resellers as a read-only changelog; the two other
    // items in this group stay superuser-only, so we drop the group flag and
    // annotate per item.
    items: [
      { to: "/system-prompts", label: msg`System prompts`, icon: "system-prompts", superuserOnly: true },
      { to: "/releases", label: msg`Releases`, icon: "releases" },
      { to: "/users", label: msg`Users`, icon: "users", superuserOnly: true },
    ] as (NavItem & { superuserOnly?: boolean })[],
  },
];

export function Layout() {
  const { logout, user, emergency } = useAuth();
  const { i18n, t } = useLingui();
  // Emergency root and any real superuser see everything. Reseller-scoped
  // accounts get a trimmed nav — matches what the cloud API accepts, so no
  // hidden 403 surprises.
  const isSuperuser = emergency || user?.role === "superuser";
  return (
    <div className="app">
      <aside className="sidebar">
        <div className="brand">
          <Link to="/customers">Jalfa AI · Admin</Link>
        </div>
        <nav>
          {NAV_GROUPS.filter((group) => isSuperuser || !group.superuserOnly).map((group) => {
            const visibleItems = group.items.filter(
              (item) =>
                isSuperuser ||
                !(item as NavItem & { superuserOnly?: boolean }).superuserOnly,
            );
            if (visibleItems.length === 0) return null;
            return (
              <div className="nav-group" key={group.key}>
                <h3>{i18n._(group.label)}</h3>
                <ul>
                  {visibleItems.map((item) => (
                    <li key={item.to}>
                      <NavLink to={item.to} end>
                        <Icon name={item.icon} />
                        <span>{i18n._(item.label)}</span>
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </nav>
        <div className="sidebar-user">
          <span className="muted whoami" title={user?.email ?? ""}>
            {emergency ? t`emergency root` : user?.name ?? user?.email ?? ""}
          </span>
          <LanguageSelector />
          <button className="ghost" onClick={logout}><Trans>Sign out</Trans></button>
        </div>
      </aside>
      <main>
        <Outlet />
      </main>
    </div>
  );
}
